import httpClient, { refreshClient } from '~/lib/http-client';
import type { AuthResponse } from '@shared/types/auth';
import { getAffiliateRef } from '~/lib/affiliateRef';

export async function apiRequestMagicLink(email: string): Promise<{ message: string }> {
  const { data } = await httpClient.post<{ message: string }>('/api/auth/magic-link/request', { email });
  return data;
}

export async function apiVerifyMagicCode(email: string, code: string): Promise<AuthResponse> {
  // Forward the first-touch affiliate ref (if any) so a new signup is attributed.
  const { data } = await httpClient.post<AuthResponse>('/api/auth/magic-link/verify', {
    email,
    code,
    ref: getAffiliateRef(),
  });
  return data;
}

export async function apiGoogleSignIn(idToken: string): Promise<AuthResponse> {
  const { data } = await httpClient.post<AuthResponse>('/api/auth/google', { idToken, ref: getAffiliateRef() });
  return data;
}

export async function apiLogout(): Promise<void> {
  await httpClient.post('/api/auth/logout');
}

let inflightRefresh: Promise<AuthResponse> | null = null;
let lastRefreshResult: { data: AuthResponse; at: number } | null = null;

export function clearRefreshCache(): void {
  lastRefreshResult = null;
}

export function apiRefreshToken(): Promise<AuthResponse> {
  if (inflightRefresh) {
    return inflightRefresh;
  }

  /*
   * Reuse recent successful refresh result (prevents Strict Mode double-call
   * from rotating the token twice and invalidating the session)
   */
  if (lastRefreshResult && Date.now() - lastRefreshResult.at < 5000) {
    return Promise.resolve(lastRefreshResult.data);
  }

  inflightRefresh = refreshClient
    .post<AuthResponse>('/api/auth/refresh')
    .then(({ data }) => {
      lastRefreshResult = { data, at: Date.now() };
      return data;
    })
    .finally(() => {
      inflightRefresh = null;
    });

  return inflightRefresh;
}
